/**
 * EMPTY · LOADING · SLOW · ERROR — as written Hebrew states. 09 §5.8.
 *
 * "Every state is a written Hebrew screen with a title, one sentence of explanation, and one
 *  obvious next step." Never a blank screen, never a full-page spinner that hides where you are,
 *  never an English exception, never a stack trace.
 *
 * Denials do NOT come through here — they have their own component, because a denial carries a
 * server decision and this one does not (see DenialNotice).
 */

import type { ReactNode } from 'react';

export interface StateBlockProps {
  readonly titleHe: string;
  readonly bodyHe: string;
  readonly tone?: 'empty' | 'error' | 'unavailable' | 'stale';
  readonly nextStep?: ReactNode;
}

export function StateBlock({ titleHe, bodyHe, tone = 'empty', nextStep }: StateBlockProps): JSX.Element {
  return (
    <section className="state-block" data-tone={tone}>
      <h2 className="state-block__title">{titleHe}</h2>
      <p className="state-block__body">{bodyHe}</p>
      {nextStep === undefined ? null : <div className="state-block__next">{nextStep}</div>}
    </section>
  );
}

/**
 * 09 §5.8 Loading: "skeleton rows in the shape of the coming table, with the context bar already
 * populated". The bar is rendered by the shell and is never behind the content (09 §3.3).
 */
export function SkeletonRows({ rows = 6 }: { readonly rows?: number }): JSX.Element {
  return (
    <div className="skeleton" aria-busy="true" aria-live="polite">
      <span className="visually-hidden">הנתונים בטעינה</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton__row" />
      ))}
    </div>
  );
}

/**
 * 09 §5.8 "Error": a short reference code the user can quote to support, and NEVER the exception
 * text (§9.1 V3).
 */
export function ErrorState({ requestUid }: { readonly requestUid: string }): JSX.Element {
  return (
    <StateBlock
      titleHe="אירעה תקלה"
      bodyHe="לא הצלחנו להשלים את הפעולה."
      tone="error"
      nextStep={
        <p className="denial__reference">
          קוד לפנייה לתמיכה: <span dir="ltr">{requestUid === '' ? 'לא זמין' : requestUid}</span>
        </p>
      }
    />
  );
}
